/**
 * Type guard for a finite, real number. Centralized so every "is this value
 * usable as a timestamp / duration?" check in this module reads the same way.
 */
export const isFiniteNumber = (value: unknown): value is number =>
  typeof value === 'number' && Number.isFinite(value);

/**
 * Range used to clip and offset host markers when only a sub-region of the
 * source timeline is exported (Premiere in/out points; After Effects Render
 * Queue work area). Both bounds are in microseconds on the source timeline.
 *
 * `endMicroseconds` MAY be `Number.POSITIVE_INFINITY` when only one side of
 * the range is set (e.g. Premiere's "in only" case).
 */
export type ExportRange = {
  startMicroseconds: number;
  endMicroseconds: number;
};

/**
 * How close (in µs) an offset may sit to the **ideal** frame boundary
 * `(round(offsetSec × fps) / fps) × 1e6` before we treat it as a whole-frame
 * host marker and snap. Host / GraphQL can truncate integer µs by a few from
 * that ideal; keep this small relative to a frame (~41.7ms at 24fps).
 */
export const FRAME_GRID_SNAP_TOLERANCE_MICROSECONDS = 4;

/**
 * Ideal start time of frame `frameIndex` (0-based) in microseconds at `fps`.
 * Result may be fractional; callers that need an integer inside the frame use
 * `ceil` with a tiny epsilon (see {@link snapToFrameGridIfNear}).
 */
function frameStartMicroseconds(frameIndex: number, fps: number): number {
  return (frameIndex / fps) * 1_000_000;
}

/**
 * When `offsetMicroseconds` is on the comp/sequence frame grid for `fps` but
 * sits **just below** the ideal boundary (common with integer µs truncation),
 * returns the first integer microsecond **inside** that frame so consumers
 * using `floor(time × fps)` match the host. Returns `null` when the offset is
 * not within {@link FRAME_GRID_SNAP_TOLERANCE_MICROSECONDS} of that boundary
 * (e.g. deliberate sub-frame markers) or when inputs are invalid.
 */
export function snapToFrameGridIfNear(
  offsetMicroseconds: number,
  fps: number
): number | null {
  if (
    !Number.isFinite(offsetMicroseconds) ||
    !Number.isFinite(fps) ||
    fps <= 0
  ) {
    return null;
  }

  const offsetSec = offsetMicroseconds / 1_000_000;
  const nearestFrameIndex = Math.round(offsetSec * fps);
  const idealStartMicroseconds = frameStartMicroseconds(nearestFrameIndex, fps);
  const distanceFromIdealMicroseconds = Math.abs(
    offsetMicroseconds - idealStartMicroseconds
  );

  if (distanceFromIdealMicroseconds > FRAME_GRID_SNAP_TOLERANCE_MICROSECONDS) {
    return null;
  }

  const snappedMicroseconds = Math.ceil(idealStartMicroseconds - 1e-9);
  return Number.isFinite(snappedMicroseconds) ? snappedMicroseconds : null;
}

/**
 * Whether a marker (start + end on the source timeline, in microseconds) sits
 * inside the export range. Range-based markers must fully fit; point markers
 * just need their start to be in `[start, end]`. When `range` is `null` (full
 * timeline export) every marker is considered in range.
 */
export const isMarkerInExportRange = (
  markerStartMicroseconds: number,
  markerEndMicroseconds: number,
  range: ExportRange | null
): boolean => {
  if (!range) return true;
  if (markerStartMicroseconds < range.startMicroseconds) return false;
  if (markerEndMicroseconds > range.endMicroseconds) return false;
  return true;
};

/**
 * Adjusted timestamp (in microseconds) for a Frame.io comment derived from a
 * host marker. When `range` is set, the marker is shifted left by
 * `range.startMicroseconds` so it lands at the right spot inside the rendered
 * file (which always starts at t=0 in Frame.io). Negative results are clamped
 * to 0 — those only happen for impossible inputs (a marker that survived
 * filtering despite being before the in-point), so 0 is the safe floor.
 *
 * The clamp also applies when `range` is `null` and `markerStartMicroseconds`
 * is itself negative; this case is silent (no telemetry) because the
 * `integrations-marker-comments-negative-offset` warn in
 * `useExportMarkersAsComments` is intentionally scoped to the with-range case
 * (where a negative offset implies a filter bug). A bare negative absolute
 * timestamp is a host/normalization issue and is already reported through
 * the `invalid-marker-start` warn for non-finite inputs.
 *
 * Optional `framesPerSecond` (when finite and > 0): timestamps that sit on
 * (or float within {@link FRAME_GRID_SNAP_TOLERANCE_MICROSECONDS} µs of) a
 * frame grid for that rate are snapped upward so integer microsecond rounding
 * does not land just **below** the frame boundary after `time × fps` math in
 * Frame.io. Omitted, `null`, or invalid values skip frame snapping.
 */
export const getCommentTimestampMicroseconds = (
  markerStartMicroseconds: number,
  range: ExportRange | null,
  framesPerSecond?: number | null
): number => {
  const offset = range
    ? markerStartMicroseconds - range.startMicroseconds
    : markerStartMicroseconds;

  if (
    framesPerSecond != null &&
    Number.isFinite(framesPerSecond) &&
    framesPerSecond > 0
  ) {
    const snapped = snapToFrameGridIfNear(offset, framesPerSecond);
    if (snapped != null) {
      return Math.max(0, snapped);
    }
  }
  return Math.max(0, Math.round(offset));
};
